import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { patchAdminSearch } from './adminDashboardNav';

const OVERLAY_KEYS = ['view', 'edit', 'action', 'history', 'mode'];
const EMPTY_KEYS = [];

export function useAdminPageSearch() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(
    () => new URLSearchParams(String(location.search || '').replace(/^\?/, '')),
    [location.search],
  );

  const get = useCallback((key) => params.get(key) || '', [params]);

  const patch = useCallback(
    (next = {}, remove = []) => {
      const search = patchAdminSearch(location.search, next, remove);
      if (search === (location.search || '')) return;
      navigate(
        { pathname: location.pathname || '/', search },
        { replace: true },
      );
    },
    [location.pathname, location.search, navigate],
  );

  return { get, patch, params, search: location.search };
}

function findById(rows, id, idKey) {
  if (!id) return null;
  return (
    (rows || []).find((row) => String(row?.[idKey]) === String(id)) || null
  );
}

/**
 * Persist list-page overlays in the URL:
 * `view=<id>`, `edit=<id>`, `action=add`, optional `tab=`, `history=<id>`.
 */
export function useAdminListOverlay(rows, options = {}) {
  const {
    idKey = 'id',
    allowedTabs,
    extraViewKeys = EMPTY_KEYS,
    extraClearKeys = EMPTY_KEYS,
  } = options;
  const { get, patch } = useAdminPageSearch();

  const viewId = get('view');
  const editId = get('edit');
  const historyId = get('history');
  const action = get('action');
  const mode = get('mode');
  const urlTab = get('tab');

  const viewing = useMemo(() => {
    const fromView = findById(rows, viewId, idKey);
    if (fromView) return fromView;
    for (const extra of extraViewKeys) {
      const value = get(extra.param);
      if (!value) continue;
      const hit = (rows || []).find(
        (row) => String(row?.[extra.rowKey]) === String(value),
      );
      if (hit) return hit;
    }
    return null;
  }, [rows, viewId, idKey, extraViewKeys, get]);

  const editing = useMemo(
    () => findById(rows, editId, idKey),
    [rows, editId, idKey],
  );
  const historyItem = useMemo(
    () => findById(rows, historyId, idKey),
    [rows, historyId, idKey],
  );

  const tab = useMemo(() => {
    if (!allowedTabs?.length) return urlTab;
    return allowedTabs.includes(urlTab) ? urlTab : allowedTabs[0];
  }, [allowedTabs, urlTab]);

  const showAdd = action === 'add';
  const showForm = showAdd || Boolean(editing);
  const overlayKeys = useMemo(
    () => [...OVERLAY_KEYS, ...extraClearKeys, ...extraViewKeys.map((k) => k.param)],
    [extraClearKeys, extraViewKeys],
  );

  const openView = useCallback(
    (row) => {
      if (!row) return;
      const extra = {};
      extraViewKeys.forEach((key) => {
        extra[key.param] = row[key.rowKey] || '';
      });
      patch({ view: row[idKey], ...extra }, ['edit', 'action', 'history', 'mode']);
    },
    [patch, idKey, extraViewKeys],
  );

  const openEdit = useCallback(
    (row) => {
      if (!row) return;
      patch({ edit: row[idKey] }, ['view', 'action', 'history', ...extraClearKeys]);
    },
    [patch, idKey, extraClearKeys],
  );

  const openAdd = useCallback(
    (extra = {}) => {
      patch({ action: 'add', ...extra }, ['view', 'edit', 'history']);
    },
    [patch],
  );

  const openHistory = useCallback(
    (row) => {
      if (!row) return;
      patch({ history: row[idKey] }, ['view', 'edit', 'action']);
    },
    [patch, idKey],
  );

  const closeOverlay = useCallback(() => {
    patch({}, overlayKeys);
  }, [patch, overlayKeys]);

  const setTab = useCallback(
    (next) => {
      const first = allowedTabs?.[0];
      patch({ tab: first && next === first ? '' : next });
    },
    [patch, allowedTabs],
  );

  return {
    get,
    patch,
    viewing,
    editing,
    historyItem,
    showAdd,
    showForm,
    action,
    mode,
    tab,
    openView,
    openEdit,
    openAdd,
    openHistory,
    closeOverlay,
    setTab,
  };
}
